import {
	AlertTriangle,
	Download,
	Eye,
	FilePlus2,
	Loader2,
	RefreshCw,
	Send,
	Trash2,
} from "lucide-react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import type { Rdo } from "../../types.ts";
import { formatRdoDateLong, formatRdoNumber } from "../../utils/format.ts";
import { hasRdoDownload } from "../../utils/status.ts";
import { RdoStatusBadge } from "../rdo-status-badge";

export interface RdoStatusBarProps {
	rdo: Rdo;
	obraKey: string;
	canUpdate: boolean;
	canFinalize: boolean;
	canDelete: boolean;
	saving: boolean;
	pending: {
		finalize: boolean;
		retry: boolean;
		revision: boolean;
		remove: boolean;
		download: boolean;
		preview: boolean;
	};
	onFinalize: () => void;
	onRetry: () => void;
	onOpenRevision: () => void;
	onDelete: () => void;
	onDownload: () => void;
	onPreview: () => void;
}

export function RdoStatusBar({
	rdo,
	obraKey,
	canUpdate,
	canFinalize,
	canDelete,
	saving,
	pending,
	onFinalize,
	onRetry,
	onOpenRevision,
	onDelete,
	onDownload,
	onPreview,
}: RdoStatusBarProps) {
	const anyPending =
		pending.finalize ||
		pending.retry ||
		pending.revision ||
		pending.remove ||
		pending.preview;

	return (
		<div className="rdo-status-bar" data-status={rdo.status}>
			<div className="rdo-status-bar__info">
				<RdoStatusBadge status={rdo.status} />
				<div className="rdo-status-bar__heading">
					<h1 className="rdo-status-bar__title">
						{formatRdoNumber(rdo.sequenceNumber, rdo.revision)}
					</h1>
					<p className="rdo-status-bar__date">
						{formatRdoDateLong(rdo.reportDate)}
					</p>
				</div>
				{saving && (
					<span className="rdo-status-bar__saving" aria-live="polite">
						<Loader2 /> Salvando…
					</span>
				)}
			</div>

			<div className="rdo-status-bar__actions">
				{rdo.status === "draft" && (
					<>
						{canDelete && (
							<Button
								variant="outline"
								onClick={onDelete}
								disabled={anyPending}
								className="rdo-status-bar__delete"
							>
								<Trash2 /> Excluir rascunho
							</Button>
						)}
						{canUpdate && (
							<Button
								variant="outline"
								onClick={onPreview}
								disabled={anyPending}
								aria-busy={pending.preview}
							>
								{pending.preview ? <Loader2 /> : <Eye />}
								Visualizar
							</Button>
						)}
						{canFinalize && (
							<Button onClick={onFinalize} disabled={anyPending}>
								{pending.finalize ? <Loader2 /> : <Send />}
								Finalizar
							</Button>
						)}
					</>
				)}

				{rdo.status === "generating" && (
					<span className="rdo-status-bar__hint" role="status">
						<Loader2 /> Gerando o PDF… a página atualiza sozinha.
					</span>
				)}

				{rdo.status === "failed" && (
					<>
						<span className="rdo-status-bar__failure" role="alert">
							<AlertTriangle />
							{rdo.failureReason
								? `Falha na geração: ${rdo.failureReason}`
								: "Falha na geração do PDF."}
						</span>
						{canFinalize && (
							<Button onClick={onRetry} disabled={anyPending}>
								{pending.retry ? <Loader2 /> : <RefreshCw />}
								Tentar novamente
							</Button>
						)}
					</>
				)}

				{hasRdoDownload(rdo.status, rdo.hasPdf) && (
					<Button
						variant={rdo.status === "finalized" ? "default" : "outline"}
						onClick={onDownload}
						disabled={pending.download}
					>
						{pending.download ? <Loader2 /> : <Download />}
						Baixar PDF
					</Button>
				)}

				{rdo.status === "finalized" && canFinalize && !rdo.supersededById && (
					<Button
						variant="outline"
						onClick={onOpenRevision}
						disabled={anyPending}
					>
						{pending.revision ? <Loader2 /> : <FilePlus2 />}
						Nova revisão
					</Button>
				)}

				{rdo.status === "superseded" && rdo.supersededById && (
					<Button asChild variant="outline">
						<Link to={`/${obraKey}/rdo/${rdo.supersededById}`}>
							Ver revisão atual
						</Link>
					</Button>
				)}
			</div>
		</div>
	);
}
